/**
 * 최종산출물(/preview) "HTML 다운로드" 버튼.
 *
 * 라이브 FinalPreview 헤더(headerAction)로 주입된다. 클릭 시 라이브 트리가 렌더에 쓰는 모든
 * 쿼리(메인 3종 + 표별 4종)를 fetchQuery 로 확보해 임베드한 뒤, 라이브와 같은 뷰어를 CSR 하는
 * self-contained HTML 을 만든다.
 *
 * (FinalPreview 가 이 버튼을 직접 import 하지 않는다 — FinalPreview 는 뷰어 번들에 재사용되므로
 *  빌더 import 가 섞이면 번들 순환이 된다. 그래서 headerAction 주입 + 빌더 동적 import.)
 */

import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/toggle-ui-kit/button";
import { collectImageDataUris, useHtmlExport } from "@/lib/html-export";
import { gatherPreviewData } from "./query-plan";
import type { FinalPreviewExportData } from "./types";

export const FinalPreviewDownloadButton = () => {
  const { workbookId } = useParams();
  const queryClient = useQueryClient();

  const { exportNow, isExporting } = useHtmlExport({
    onError: () => toast.error("HTML 다운로드에 실패했습니다."),
    build: async () => {
      if (!workbookId) return null;

      // 라이브 트리가 렌더에 쓰는 모든 쿼리를 단일 출처(query-plan)로 확보.
      const gathered = await gatherPreviewData(queryClient, workbookId);
      if (!gathered.tables.length) {
        toast.warning("다운로드할 표가 없습니다.");
        return null;
      }

      // 라이브 DOM 의 절대경로 이미지(예: Section 펼침 아이콘)를 data URI 로 임베드.
      const imageAssets = await collectImageDataUris();
      const data: FinalPreviewExportData = { ...gathered, imageAssets };

      // 라이브 헤더와 동일하게 dsd 파일 목록에서 워크북명을 찾아 파일명에 쓴다.
      const baseName =
        gathered.dsdFiles
          .find((file) => file.id === workbookId)
          ?.name?.trim() || "최종산출물";

      // 무거운 뷰어 번들(React + FinalPreview 트리)은 다운로드 시점에만 동적 import.
      const { buildFinalPreviewViewerHtml } = await import(
        "./build-viewer-html"
      );
      const html = buildFinalPreviewViewerHtml(data, `${baseName} 최종산출물`);
      return { html, filename: `${baseName}_최종산출물.html` };
    },
  });

  return (
    <Button
      variant="outlined"
      color="primary"
      size={32}
      leftIcon={
        isExporting ? <Loader2 className="animate-spin" /> : <Download />
      }
      disabled={!workbookId || isExporting}
      onClick={exportNow}
    >
      {isExporting ? "준비 중..." : "HTML 다운로드"}
    </Button>
  );
};
